import { GetStaticPaths, GetStaticProps } from "next"
import { Container, Box, Grid, List, ListItemButton, ListItemText } from "@mui/material"
import { makeStyles } from "makeStyles"
import * as prismic from "@prismicio/client"
import Link from "components/i18n/Link"
import { ArticleJsonLd } from "next-seo"
import Icon from "@mdi/react"
import { mdiCalendarClock } from "@mdi/js"

import SEO from "components/SEO"
import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"
import { SliceZoneItem } from "components/Prismic/Components"
import { Post } from "typings/Content"
import SocialMedia from "components/Prismic/SocialMedia"

import fetchLatestMasterRef from "utils/prismic/fetchLatestMasterRef"
import fetchBlogPostByUid from "utils/prismic/graphql/queries/fetchBlogPostByUid"
import fetchAllBlogs from "utils/prismic/graphql/queries/fetchAllBlogs"
import { getCountryNameFromLocale } from "components/BreadCrumbs"
import { NEXT_PUBLIC_HOST } from "utils/envVariables"
import { supportedValidLocales } from "@secureo/common/utils/i18n/supportedLocalesAndCurrencyCodes"
import { getServerTranslations } from "utils/i18n/getServerTranslations"

const useStyles = makeStyles()({
	header: {
		paddingTop: "10px",
		textAlign: "center",
	},
	card: {
		backgroundColor: "white",
		padding: "20px",
		marginTop: "20px",
		boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.05)",
		borderRadius: 5,
	},
})

const BlogPostPage = ({ post, allBlogs, locale }: Props) => {
	const { classes } = useStyles()

	const {
		_meta: { firstPublicationDate, lastPublicationDate, uid },
		previewimage,
		metadescription,
		body,
		date,
		author: {
			avatar: { url },
		},
	} = post
	const title = prismic.asText(post.title as any)
	const { edges } = allBlogs

	const breadCrumb: BreadCrumbAncestor = {
		label: title,
		href: `/blog/${uid}`,
	}

	const blogBreadCrumb: BreadCrumbAncestor = {
		label: "Blog",
		href: "/blog",
	}

	const LocalizedTitle = `SafeHero ${getCountryNameFromLocale(locale)}`

	return (
		<div
			style={{
				background:
					"url(https://res.cloudinary.com/safehero/image/upload/v1571664485/HelpCenter/UeberSafeHeroBg_oxvk5v.png)",
			}}
		>
			<div style={{ marginTop: "-30px", marginLeft: "-20px" }}>
				<BreadCrumbs breadCrumbsPaths={[blogBreadCrumb]} currentPath={breadCrumb} />
			</div>
			<Box my={5} pt={3} pb={5}>
				<SEO
					title={`${title} | ${LocalizedTitle}`}
					description={metadescription}
					asPath={`/blog/${uid}`}
					pageType="blogPost"
					// openGraph={{
					// 	images: [
					// 		{
					// 			url: previewimage.url,
					// 		},
					// 	],
					// }}
				/>
				<ArticleJsonLd
					type="BlogPosting"
					url={`${NEXT_PUBLIC_HOST}/${locale}/blog/${uid}`}
					title={title}
					images={[previewimage.url]}
					datePublished={firstPublicationDate}
					dateModified={lastPublicationDate}
					publisherName="Secureon GmbH"
					authorName={post.author.name[0].text}
					description={post.metadescription}
					isAccessibleForFree
				/>
				<Container>
					<div className={classes.header}>
						<h1>{title}</h1>
						<div style={{ display: "flex", alignItems: "center", margin: "auto" }}>
							<Icon path={mdiCalendarClock} size={0.9} color="#7abfac" />{" "}
							<Box ml={1}>{date as any}</Box>
						</div>
					</div>
					<Grid container spacing={3}>
						<Grid item xs={12} md={8}>
							<div className={classes.card}>
								{body.map((postSliceZoneItem, i) => {
									const { type } = postSliceZoneItem
									return (
										<SliceZoneItem
											key={`${type}-${i}`}
											item={postSliceZoneItem}
										/>
									)
								})}
							</div>
						</Grid>
						<Grid item xs={12} md={4}>
							<div className={classes.card}>
								<h3 style={{ marginBottom: 0, paddingLeft: 15 }}>
									Wir empfehlen auch:
								</h3>
								<List component="nav" aria-label="secondary mailbox folders">
									{edges.map((edge) => {
										const {
											node: {
												_meta: { uid },
											},
										} = edge
										const title = edge.node.title[0].text
										return (
											<Link href={`/blog/${uid}`} key={uid}>
												<ListItemButton>
													<ListItemText primary={title} />
												</ListItemButton>
											</Link>
										)
									})}
									<Link href="/blog">
										<ListItemButton>
											<ListItemText primary="Alle anzeigen" />
										</ListItemButton>
									</Link>
								</List>
							</div>
							<div className={classes.card}>
								<div style={{ display: "flex", alignItems: "center" }}>
									<img
										alt="author"
										src={url}
										style={{
											height: "80px",
											width: "80px",
											background: "grey",
											borderRadius: "80px",
											objectFit: "cover",
										}}
									/>
									<Box ml={2}>
										<strong>{post.author.name[0].text}</strong>
									</Box>
								</div>
								<p>{post.author.description[0].text}</p>
							</div>
							<div className={classes.card}>
								<SocialMedia />
							</div>
						</Grid>
					</Grid>
				</Container>
			</Box>
		</div>
	)
}

export const getStaticPaths: GetStaticPaths = async () => {
	const masterRef = await fetchLatestMasterRef()
	const allBlogs = await fetchAllBlogs(masterRef)

	const slugs: string[] = allBlogs?.edges.map(({ node }) => node._meta.uid)

	const paths = slugs
		.map((slug) => {
			return supportedValidLocales.map((locale) => {
				return { params: { slug }, locale }
			})
		})
		.flat()

	return {
		paths,
		fallback: false,
	}
}

export const getStaticProps: GetStaticProps = async (context) => {
	const { locale } = context
	const slug = (context.params?.slug as string) ?? ""
	const masterRef = await fetchLatestMasterRef()
	const post = await fetchBlogPostByUid(masterRef, slug)
	const allBlogs = await fetchAllBlogs(masterRef)

	const props = {
		post,
		allBlogs,
		locale,
		...(await getServerTranslations(locale, ["common", "countries", "languages"])),
	}

	return {
		props,
	}
}

interface Props {
	post: Post
	allBlogs: any
	locale: string
}

export default BlogPostPage
